import React from 'react';
import { connect } from 'react-redux';
import { List, Map } from 'immutable';
import store from '../../store/store';

const mapStateToProps = (state) => ({
  userRoleConfigs: state.getIn(['session', 'roleConfigs'], new List()),
  userReportConfigs: state.getIn(['session', 'reportConfigs'], new List()),
});

export const PositionBookConfigName = {
  UREA: 'UREA',
  AN: 'AN',
  AS: 'AS',
  UAN: 'UAN',
  PHOSPHATES: 'PHOSPHATES',
  POTASH: 'POTASH',
  NPKS: 'NPKS',
  AGRI: 'AGRI',
  SULFUR: 'SULFUR',
  SOLUBLES: 'SOLUBLES',
  OTHER: 'OTHER',
};

export const ReportName = {
  TODAYS_PAYMENTS: 'TODAYS_PAYMENTS',
  POSITION_MAP: 'POSITION_MAP',
  POSITION_BOOK: 'POSITION_BOOK',
  OPEN_POSITION: 'OPEN_POSITION',
  PROFITABILITY: 'PROFITABILITY',
  VOLUME: 'VOLUME',
  PAYMENTS: 'PAYMENTS',
  PERFORMANCE: 'PERFORMANCE',
  ANALYTICS: 'ANALYTICS',
  CUSTOM_REPORTS: 'CUSTOM_REPORTS',
  CUSTOM_REPORTS_REGIONAL: 'CUSTOM_REPORTS_REGIONAL',
  USER_PERFORMANCE: 'USER_PERFORMANCE',
  MULTINODAL: 'MULTINODAL',
  SUPPLY_DEMAND: 'SUPPLY_DEMAND',
  EARNING_STATEMENTS: 'EARNING_STATEMENTS',

  positionBook: (productType) => PositionBookConfigName.hasOwnProperty(productType)
    && `POSITION_BOOK_${productType}`,
};

export const getAllPositionBookTypeConfigs = () => Object.keys(PositionBookConfigName)
  .map(key => ReportName.positionBook(PositionBookConfigName[key]));

const hasReportPermissions = (reportConfigs, reportNames) =>
  reportNames && reportConfigs && reportConfigs.includes &&
    reportNames.some && reportNames.some(reportName => reportConfigs.includes(reportName));

export const userHasReportPermissions = (reportNames) =>
  hasReportPermissions(store.getState().getIn(['session', 'reportConfigs'], new List()),
    reportNames);

export const hasAnyReportPermission = () =>
  (store.getState().getIn(['session', 'reportConfigs'], new List()) || new List()).size > 0;

export const isResponsibleForAnySupplyDemand = () =>
  (store.getState().getIn(['session', 'supplyDemandResponsibles']) || new List()).size !== 0;

const resolveResponsibles = (currentUserId, deal) => {
  let responsibles = null;
  if (deal) {
    if (deal.get('id') === undefined) {
      return [currentUserId];
    }
    responsibles = [deal.get('creator') || -1];
    if (deal.get('responsibles')) {
      return responsibles.concat(deal.get('responsibles').toJS());
    }
  }
  return responsibles;
};

const isUserAuthorInChain = (deal, requestedPermissionLevel = 1, realPermissionLevel) => {
  const state = store.getState();
  const currentUserId = state.getIn(['session', 'id']);
  if (resolveResponsibles(currentUserId, deal).some(r => r == currentUserId)) {
    if (realPermissionLevel != undefined) {
      return realPermissionLevel >= requestedPermissionLevel;
    }
    return true;
  }
  if (requestedPermissionLevel == 1) {
    let links = deal.get('links', new List());
    deal.get('products', new List()).forEach(p => {
      links = links.concat(p.get('purchaseLink')).concat(p.get('saleLink'));
    });
    links = links.filter(l => l);
    const dealsList = state.getIn(['data', 'deals', 'list'], new Map());
    return links.some(link => {
      const purchase = dealsList.get('purchase', new List())
        .find(p => p.get('id') == link.get('purchaseId'));
      const sale = dealsList.get('sale', new List())
        .find(s => s.get('id') == link.get('saleId'));
      const transport = dealsList.get('transport', new List())
        .find(t => t.get('id') == link.get('transport'));
      if (purchase && resolveResponsibles(currentUserId, purchase).some(r => r == currentUserId)) {
        return true;
      }
      if (sale && resolveResponsibles(currentUserId, sale).some(r => r == currentUserId)) {
        return true;
      }
      return transport && resolveResponsibles(currentUserId, transport)
        .some(r => r == currentUserId);
    });
  }
  return false;
};

export const hasPermission =
  (userRoleConfigs = store.getState().getIn(['session', 'roleConfigs']),
    permissions, deal, responsibles, isVessel) => {
    if (!permissions) {
      return false;
    }
    return (userRoleConfigs || new List()).some(roleConfig => permissions.some(p => {
      const permissionArray = p.split('#');
      if (permissionArray[0].startsWith('my')) {
        if (responsibles) {
          return permissionArray.length > 1 ?
            isUserAuthorInChain(deal, permissionArray[1], roleConfig.get(permissionArray[0])) ||
            (roleConfig.get(permissionArray[0]) >= permissionArray[1]
            && (permissionArray[0] == 'myTransports' && isVessel))
            :
            roleConfig.get(permissionArray[0]) && isUserAuthorInChain(deal);
        }
        return permissionArray.length > 1 ?
          roleConfig.get(permissionArray[0]) >= permissionArray[1]
          :
          roleConfig.get(permissionArray[0]);
      }
      if (permissionArray[0].startsWith('all') || permissionArray[0].startsWith('references')) {
        return roleConfig.get(permissionArray[0]) >= permissionArray[1];
      }
      return roleConfig.get(p);
    }));
  };

export const validateRoleConfig = (permissions, deal) => {
  let isVessel = false;
  if (deal) {
    isVessel = deal.get('transportType') == 'BULK';
  }
  return hasPermission(store.getState().getIn(['session', 'roleConfigs'], new List()),
    permissions, deal,
    resolveResponsibles(store.getState().getIn(['session', 'id']), deal), isVessel);
};

export const getUserRegionId = () => store.getState().getIn(['session', 'origin']);
export const getUserCustomReportRegionId = () =>
  store.getState().getIn(['session', 'customReportOrigins']);

export const userHasDealRegion = (deal) => {
  const dealRegion = deal.get('origin') || deal.get('origin') == 0
    || deal.get('destination') || deal.get('destination') == 0;
  if (dealRegion) {
    return dealRegion == getUserRegionId();
  }
  return false;
};

export const isUserDeal = (deal) => {
  let responsibles = null;
  if (deal) {
    responsibles = [deal.get('creator') || -1];
    if (deal.get('responsibles')) {
      responsibles = responsibles.concat(deal.get('responsibles').toJS());
    }
  }
  return responsibles.some(r => r == store.getState().getIn(['session', 'id']));
};

export const isUserPayment = (payment, userId, purchases, sales, transports) => {
  if (payment.get('operationOfficer') == userId) {
    return true;
  }
  const isID = (id) => id || id === 0;
  if (isID(payment.get('purchase'))) {
    return isUserDeal(purchases.find(p => p.get('id') == payment.get('purchase'), this, new Map()));
  }
  if (isID(payment.get('sale'))) {
    return isUserDeal(sales.find(p => p.get('id') == payment.get('sale'), this, new Map()));
  }
  if (isID(payment.get('transport'))) {
    return isUserDeal(transports.find(p => p.get('id')
      == payment.get('transport'), this, new Map()));
  }
  return false;
};


const hasAnyRole = (userRoleConfigs, roles) =>    // eslint-disable-line
  roles ? userRoleConfigs.some(roleConfig =>
    roles.some(r => r == roleConfig.get('rolename'))) : false;

export const hasOnlyRoles = (roles) => {
  if (roles) {
    const userRoles = store.getState()
      .getIn(['session', 'roleConfigs'], new List()).map(rc => rc.get('rolename'));
    return roles.every(r => userRoles.includes(r)) && roles.length == userRoles.size;
  }
  return false;
};

export const hasUserAnyRole = (rolesList) =>
  hasAnyRole(store.getState().getIn(['session', 'roleConfigs'], new List()), rolesList);

export const validateComplex = (rolesList, permissions) =>
  hasUserAnyRole(rolesList) && validateRoleConfig(permissions);

class RolesConfigFilterClass extends React.Component {
  getChildContext() {
    if (!hasAnyRole(this.props.userRoleConfigs, this.props.roles)
      || !hasPermission(this.props.userRoleConfigs, this.props.permissions)
      || !hasReportPermissions(this.props.userReportConfigs, this.props.reportPermissions)) {
      if (this.props.method == 'disable') {
        return { disabled: true };
      }
      if (this.props.method == 'readonly') {
        return { readonly: true };
      }
    }
    return null;
  }

  render() {
    if (this.props.method == 'disable'
      || this.props.method == 'readonly'
      || hasAnyRole(this.props.userRoleConfigs, this.props.roles)
      || hasPermission(this.props.userRoleConfigs, this.props.permissions)
      || hasReportPermissions(this.props.userReportConfigs, this.props.reportPermissions)) {
      return this.props.children;
    }
    return null;
  }
}

RolesConfigFilterClass.propTypes = {
  permissions: React.PropTypes.array,
  reportPermissions: React.PropTypes.array,
  roles: React.PropTypes.array,
  userRoleConfigs: React.PropTypes.object.isRequired,
  userReportConfigs: React.PropTypes.object.isRequired,
  children: React.PropTypes.node,
  method: React.PropTypes.oneOf(['hide', 'disable', 'readonly']),
};


RolesConfigFilterClass.childContextTypes = {
  disabled: React.PropTypes.bool,
  readonly: React.PropTypes.bool,
};

export default connect(mapStateToProps)(RolesConfigFilterClass);
